import { IEducation, IJob, IPodcast } from '@types';

import { client } from 'apollo-client';
import { gql } from '@apollo/client';
import { mapEducation } from 'utils/mappings/mapEducation';
import { mapJobs } from 'utils/mappings/mapJobs';
import { NextPage } from 'next';

import { Button } from 'Atoms/Button';
import { Container } from 'Atoms/Container';
import { Education } from 'Organisms/Education';
import { Icon } from 'Atoms/Icon';
import { WorkExperience } from 'Organisms/WorkExperience';
import { AnimatePage } from 'Atoms/AnimatePage';
import { useRouter } from 'next/router';
import { SeoHead } from 'Atoms/SeoHead';

interface IProps {
	podcasts: IPodcast[];
	jobs: IJob[];
	education: IEducation[];
}

const AboutPage: NextPage<IProps> = ({ jobs, education }) => {
	const router = useRouter();
	return (
		<AnimatePage>
			<SeoHead
				title="About Deepankar Sharma - Software Engineer from India"
				description="As a passionate software engineer, I create amazing web apps to make the internet a better and more secure place."
			/>
			<Container>
				<h1 className="headline text-3xl md:text-5xl lg:text-6xl mt-8">
					Deepankar Sharma
				</h1>
				<h2 className="mx-1 font-bold text-xl md:text-2xl mt-2">
					Senior Software Engineer
				</h2>
				<p className="mt-8">
					Over the past six years, I&apos;ve honed my expertise in software development, problem-solving, and scaling applications for real-world challenges.
					My sweet spot lies in optimizing tech stacks, particularly when Python, cloud infrastructure, or serverless solutions come into play.
				</p>

				<p className="mt-4">
					When I&apos;m not solving complex engineering puzzles or scaling systems, you&apos;ll find me chasing new ideas,
					or hitting the court for a round of badminton, a swim, or playing table-tennis 🏓.
					Being an introverted soul, I often find myself researching about random topics and ideas on the internet (
					<span className='italic opacity-75'>curiosity killed the cat, but satisfaction brought it back </span> 😌).
					These days, I am particularly interested in security and personal finance / investing.
				</p>

				<h2 className="headline mt-12 mb-4 text-4xl">Experience</h2>

				<WorkExperience jobs={jobs} />

				<h2 className="headline mt-12 mb-4 text-4xl">Education</h2>
				<p className="mb-6">
					I have a bachelor&apos;s degree in Information Technology, but I am mostly self-taught in the technologies  and tools I use today.
					Here are some of the most relevant courses I have completed:
				</p>

				<Education education={education} />

				<div className="flex justify-center mt-8">
					<Button
						className="group flex gap-2 whitespace-nowrap"
						onClick={() => router.push("/resume")}
					>
						<div className="w-6 text-blue-500 group-hover:text-off-white dark:text-purple-500">
							<Icon icon="DOWNLOAD" />
						</div>
						<div className="block headline font-normal group-hover:text-off-white">
							Download Resume
						</div>
					</Button>
				</div>
			</Container>
		</AnimatePage>
	);
};

export async function getStaticProps() {
	const { data } = await client.query({
		query: gql`
			query AboutPageQuery {
				jobs(orderBy: toDate_DESC) {
					id
					jobTitle
					fromDate
					toDate
					description
					company {
						name
						url
						logo {
							url
						}
					}
					skills
				}
				educations(orderBy: endDate_DESC) {
					id
					course
					startDate
					endDate
					skills
					courseCertificate
					institute {
						name
						url
						logo {
							url
						}
					}
				}
			}
		`,
	});

	return {
		props: {
			education: mapEducation(data.educations),
			jobs: mapJobs(data.jobs),
		},
	};
}

export default AboutPage;
